// frontend/UploadInvoice.jsx

// Admin page: ended trips of agent bookings, agent wise, with invoice upload.

// Create React App compatible. No extra dependencies. Styles are embedded below.

//

//   GET    /endtrips/invoices/summary?from=&to=

//   GET    /endtrips/invoices?agent_id=&search=&status=&from=&to=&page=&limit=

//   POST   /endtrips/invoices/:enqId      (multipart: invoice_no, invoice)

//   DELETE /endtrips/invoices/:enqId

//

// Route:   <Route path="/endtrips/upload-invoice" element={<UploadInvoice />} />

import { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

// where /uploads/... files are served from (same host as the API by default)

const FILE_BASE = process.env.REACT_APP_FILE_URL || API_BASE.replace(/\/api\/v1\/?$/i, "");

const PAGE_SIZE = 20;

const MAX_MB = 10;

const DEBUG = true; // set to false when everything works

/* ----------------------------- helpers ----------------------------- */

const log = (...a) =>

  DEBUG && console.log("%c[UploadInvoice]", "color:#FF3D8B;font-weight:bold", ...a);

const authHeaders = (json = true) => {

  const token = localStorage.getItem("token");

  return {

    ...(json ? { "Content-Type": "application/json" } : {}),

    ...(token ? { Authorization: `Bearer ${token}` } : {}),

  };

};

async function api(path, { method = "GET", body } = {}) {

  const url = `${API_BASE}${path}`;

  log("→", method, url);

  let res;

  try {

    res = await fetch(url, {

      method,

      headers: authHeaders(!(body instanceof FormData)), // let the browser set the multipart boundary

      body,

    });

  } catch (e) {

    console.error("[UploadInvoice] network/CORS error", url, e);

    throw new Error("Cannot reach the server (network or CORS error)");

  }

  const raw = await res.text();

  let json;

  try {

    json = JSON.parse(raw);

  } catch {

    console.error("[UploadInvoice] non-JSON response", res.status, raw.slice(0, 200));

    throw new Error(`Server returned non-JSON (HTTP ${res.status})`);

  }

  log("←", res.status, json.debug || "");

  if (!res.ok || json.success === false) {

    throw new Error(json.debug || json.message || `Request failed (HTTP ${res.status})`);

  }

  return json;

}

const PALETTE = [

  { bg: "#FFE0EC", fg: "#E0246B" },

  { bg: "#E3E0FF", fg: "#4A35E0" },

  { bg: "#D2F5EE", fg: "#0A8F7B" },

  { bg: "#FFEBCB", fg: "#C46A00" },

  { bg: "#DDF0FF", fg: "#1479C9" },

  { bg: "#EBE0FF", fg: "#8A2BE2" },

  { bg: "#E2F7D5", fg: "#3F8F10" },

];

const colorFor = (id) => PALETTE[Math.abs(Number(id) || 0) % PALETTE.length];

const initials = (name, id) => {

  if (!name) return String(id).slice(-2);

  const p = name.trim().split(/\s+/);

  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "A";

};

const toDate = (s) => new Date(String(s).replace(" ", "T"));

const fmtDay = (s) =>

  s ? toDate(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

const fmtTime = (s) =>

  s ? toDate(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";

const fmtDate = (s) =>

  s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";

const fmtMoney = (v) =>

  `₹${(Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dayLabel = (s) => {

  const d = toDate(s);

  const t = new Date();

  const y = new Date();

  y.setDate(t.getDate() - 1);

  if (d.toDateString() === t.toDateString()) return "Today";

  if (d.toDateString() === y.toDateString()) return "Yesterday";

  return fmtDay(s);

};

const agentLabel = (a) => a.agent_name || `Agent #${a.agent_id}`;

/* ----------------------------- component ----------------------------- */

export default function UploadInvoice() {

  const [agents, setAgents] = useState([]);

  const [agentsLoading, setAgentsLoading] = useState(true);

  const [selected, setSelected] = useState(null);

  const [agentFilter, setAgentFilter] = useState("");

  const [trips, setTrips] = useState([]);

  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");

  const [search, setSearch] = useState("");

  const [status, setStatus] = useState("all");

  const [from, setFrom] = useState("");

  const [to, setTo] = useState("");

  const [page, setPage] = useState(1);

  // upload modal

  const [target, setTarget] = useState(null);

  const [invoiceNo, setInvoiceNo] = useState("");

  const [file, setFile] = useState(null);

  const [saving, setSaving] = useState(false);

  const [modalErr, setModalErr] = useState("");

  const [toast, setToast] = useState(null);

  const showToast = (type, msg) => {

    setToast({ type, msg });

    setTimeout(() => setToast(null), 3500);

  };

  useEffect(() => {

    const t = setTimeout(() => {

      setSearch(searchInput.trim());

      setPage(1);

    }, 350);

    return () => clearTimeout(t);

  }, [searchInput]);

  const loadAgents = useCallback(async () => {

    setAgentsLoading(true);

    try {

      const qs = new URLSearchParams();

      if (from) qs.set("from", from);

      if (to) qs.set("to", to);

      const json = await api(`/endtrips/invoices/summary?${qs}`);

      setAgents(Array.isArray(json.agent_wise) ? json.agent_wise : []);

    } catch (e) {

      console.error("[UploadInvoice] loadAgents failed:", e);

      setError(e.message);

    } finally {

      setAgentsLoading(false);

    }

  }, [from, to]);

  const loadTrips = useCallback(async () => {

    setLoading(true);

    setError("");

    try {

      const qs = new URLSearchParams({ page, limit: PAGE_SIZE, status });

      if (selected) qs.set("agent_id", selected);

      if (search) qs.set("search", search);

      if (from) qs.set("from", from);

      if (to) qs.set("to", to);

      const json = await api(`/endtrips/invoices?${qs}`);

      setTrips(Array.isArray(json.data) ? json.data : []);

      setPagination(json.pagination || { page: 1, pages: 1, total: 0 });

    } catch (e) {

      console.error("[UploadInvoice] loadTrips failed:", e);

      setError(e.message);

    } finally {

      setLoading(false);

    }

  }, [page, status, selected, search, from, to]);

  useEffect(() => {

    loadAgents();

  }, [loadAgents]);

  useEffect(() => {

    loadTrips();

  }, [loadTrips]);

  const totals = useMemo(

    () =>

      agents.reduce(

        (t, a) => ({

          trips: t.trips + (Number(a.total_trips) || 0),

          pending: t.pending + (Number(a.pending) || 0),

          uploaded: t.uploaded + (Number(a.uploaded) || 0),

          today: t.today + (Number(a.today_trips) || 0),

        }),

        { trips: 0, pending: 0, uploaded: 0, today: 0 }

      ),

    [agents]

  );

  const maxTrips = useMemo(

    () => Math.max(1, ...agents.map((a) => Number(a.total_trips) || 0)),

    [agents]

  );

  const selectedAgent = agents.find((a) => a.agent_id === selected);

  const view = selectedAgent

    ? {

        trips: Number(selectedAgent.total_trips),

        pending: Number(selectedAgent.pending),

        uploaded: Number(selectedAgent.uploaded),

      }

    : totals;

  const rate = view.trips ? Math.round((view.uploaded / view.trips) * 100) : 0;

  const visibleAgents = agents.filter(

    (a) =>

      agentLabel(a).toLowerCase().includes(agentFilter.toLowerCase()) ||

      String(a.agent_id).includes(agentFilter) ||

      (a.company_name || "").toLowerCase().includes(agentFilter.toLowerCase())

  );

  const grouped = useMemo(() => {

    const map = new Map();

    trips.forEach((t) => {

      const k = t.trip_end_time ? dayLabel(t.trip_end_time) : "No date";

      if (!map.has(k)) map.set(k, []);

      map.get(k).push(t);

    });

    return [...map.entries()];

  }, [trips]);

  const selectAgent = (id) => {

    setSelected(id);

    setPage(1);

  };

  const clearFilters = () => {

    setSearchInput("");

    setSearch("");

    setStatus("all");

    setFrom("");

    setTo("");

    setPage(1);

  };

  const refresh = () => {

    loadAgents();

    loadTrips();

  };

  /* ---------- upload / delete ---------- */

  const openModal = (t) => {

    setTarget(t);

    setInvoiceNo(t.invoice_no || "");

    setFile(null);

    setModalErr("");

  };

  const closeModal = () => {

    if (!saving) setTarget(null);

  };

  const onPickFile = (e) => {

    const f = e.target.files?.[0];

    if (!f) return;

    if (f.size > MAX_MB * 1024 * 1024) {

      setModalErr(`File must be ${MAX_MB} MB or smaller`);

      e.target.value = "";

      return;

    }

    setModalErr("");

    setFile(f);

  };

  const submit = async (e) => {

    e.preventDefault();

    if (!invoiceNo.trim()) return setModalErr("Invoice number is required");

    if (!file) return setModalErr("Please choose an invoice file (PDF, JPG or PNG)");

    const fd = new FormData();

    fd.append("invoice_no", invoiceNo.trim());

    fd.append("invoice", file);

    setSaving(true);

    setModalErr("");

    try {

      await api(`/endtrips/invoices/${target.enq_id}`, { method: "POST", body: fd });

      setTarget(null);

      showToast("ok", "Invoice uploaded. The agent can now view it in the Agent App.");

      refresh();

    } catch (err) {

      setModalErr(err.message);

    } finally {

      setSaving(false);

    }

  };

  const removeInvoice = async (t) => {

    if (!window.confirm(`Remove invoice ${t.invoice_no} for ${t.reference_no}?`)) return;

    try {

      await api(`/endtrips/invoices/${t.enq_id}`, { method: "DELETE" });

      showToast("ok", "Invoice removed");

      refresh();

    } catch (err) {

      showToast("bad", err.message);

    }

  };

  const hasFilters = search || status !== "all" || from || to;

  const col = selectedAgent ? colorFor(selectedAgent.agent_id) : { bg: "#FFE0EC", fg: "#E0246B" };

  return (

    <div className="iv">

      <style>{css}</style>

      {/* header */}

      <header className="iv-head">

        <div className="iv-head-text">

          <span className="iv-eyebrow">End trips</span>

          <h1>Upload invoice</h1>

          <p>Ended trips of agent bookings, grouped agent wise. Upload the invoice and the agent sees it in the app.</p>

        </div>

        <div className="iv-stats">

          <Stat tone="pink" value={totals.trips} label="Ended trips" />

          <Stat tone="violet" value={agents.length} label="Agents" />

          <Stat tone="teal" value={totals.uploaded} label="Invoice uploaded" />

          <Stat tone="orange" value={totals.pending} label="Invoice pending" />

        </div>

      </header>

      <div className="iv-body">

        {/* agent rail */}

        <aside className="iv-rail" aria-label="Agents">

          <div className="iv-rail-top">

            <input

              className="iv-input"

              type="search"

              placeholder="Find an agent or company"

              value={agentFilter}

              onChange={(e) => setAgentFilter(e.target.value)}

              aria-label="Find an agent"

            />

          </div>

          <ul className="iv-agent-list">

            <li>

              <button

                className={`iv-agent ${selected === null ? "is-active" : ""}`}

                onClick={() => selectAgent(null)}

              >

                <span className="iv-avatar all">All</span>

                <span className="iv-agent-main">

                  <strong>All agents</strong>

                  <small>{totals.trips} ended trips</small>

                </span>

                {totals.pending > 0 && <span className="iv-count warn">{totals.pending}</span>}

              </button>

            </li>

            {agentsLoading &&

              [1, 2, 3, 4].map((i) => (

                <li key={i} className="iv-skel-row">

                  <span className="iv-skel circle" />

                  <span className="iv-skel line" />

                </li>

              ))}

            {!agentsLoading && visibleAgents.length === 0 && (

              <li className="iv-rail-empty">

                {agents.length === 0 ? "No ended trips found." : `No agents match “${agentFilter}”.`}

              </li>

            )}

            {visibleAgents.map((a) => {

              const c = colorFor(a.agent_id);

              const pct = Math.round((Number(a.total_trips) / maxTrips) * 100);

              return (

                <li key={a.agent_id}>

                  <button

                    className={`iv-agent ${selected === a.agent_id ? "is-active" : ""}`}

                    onClick={() => selectAgent(a.agent_id)}

                    style={{ "--agent-fg": c.fg, "--agent-bg": c.bg }}

                  >

                    <span className="iv-avatar" style={{ background: c.bg, color: c.fg }}>

                      {initials(a.agent_name, a.agent_id)}

                    </span>

                    <span className="iv-agent-main">

                      <strong>{agentLabel(a)}</strong>

                      <small>

                        {a.company_name ? `${a.company_name} · ` : ""}

                        {a.uploaded}/{a.total_trips} invoiced

                      </small>

                      <span className="iv-meter">

                        <span style={{ width: `${pct}%`, background: c.fg }} />

                      </span>

                    </span>

                    <span

                      className={`iv-count ${Number(a.pending) > 0 ? "warn" : ""}`}

                      style={Number(a.pending) > 0 ? undefined : { background: c.fg }}

                      title={Number(a.pending) > 0 ? "Invoices pending" : "All invoiced"}

                    >

                      {Number(a.pending) > 0 ? a.pending : "✓"}

                    </span>

                  </button>

                </li>

              );

            })}

          </ul>

        </aside>

        {/* main */}

        <main className="iv-main">

          <section className="iv-banner" style={{ "--b-bg": col.bg, "--b-fg": col.fg }}>

            <span className="iv-avatar lg">

              {selectedAgent ? initials(selectedAgent.agent_name, selectedAgent.agent_id) : "All"}

            </span>

            <div className="iv-banner-text">

              <h2>{selectedAgent ? agentLabel(selectedAgent) : "All agents"}</h2>

              <p>

                {selectedAgent

                  ? `${selectedAgent.company_name ? selectedAgent.company_name + " · " : ""}Agent ID ${selectedAgent.agent_id} · Last trip ended ${fmtDay(selectedAgent.last_trip_at)}, ${fmtTime(selectedAgent.last_trip_at)}`

                  : "Showing the latest ended trips from every agent."}

              </p>

            </div>

            <div className="iv-mini">

              <div><strong>{view.trips}</strong><span>Ended trips</span></div>

              <div><strong>{view.pending}</strong><span>Pending</span></div>

              <div><strong>{view.uploaded}</strong><span>Uploaded</span></div>

              <div><strong>{rate}%</strong><span>Invoiced</span></div>

            </div>

          </section>

          <section className="iv-filters">

            <div className="iv-search">

              <input

                className="iv-input"

                type="search"

                placeholder="Search booking id, guest, mobile or invoice no."

                value={searchInput}

                onChange={(e) => setSearchInput(e.target.value)}

                aria-label="Search trips"

              />

            </div>

            <div className="iv-seg" role="tablist" aria-label="Invoice status">

              {[

                ["all", "All"],

                ["pending", "Pending"],

                ["uploaded", "Uploaded"],

              ].map(([v, l]) => (

                <button

                  key={v}

                  role="tab"

                  aria-selected={status === v}

                  className={status === v ? "is-on" : ""}

                  onClick={() => {

                    setStatus(v);

                    setPage(1);

                  }}

                >

                  {l}

                </button>

              ))}

            </div>

            <label className="iv-date">

              <span>Trip ended from</span>

              <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />

            </label>

            <label className="iv-date">

              <span>To</span>

              <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />

            </label>

            {hasFilters && (

              <button className="iv-btn link" onClick={clearFilters}>

                Clear filters

              </button>

            )}

          </section>

          {error && (

            <div className="iv-alert" role="alert">

              <span>{error}</span>

              <button className="iv-btn link" onClick={refresh}>

                Try again

              </button>

            </div>

          )}

          <section className="iv-list" aria-busy={loading}>

            {loading &&

              [1, 2, 3].map((i) => (

                <div key={i} className="iv-card skeleton">

                  <span className="iv-skel circle" />

                  <div className="iv-skel-col">

                    <span className="iv-skel line w40" />

                    <span className="iv-skel line w80" />

                  </div>

                </div>

              ))}

            {!loading && !error && trips.length === 0 && (

              <div className="iv-empty">

                <div className="iv-empty-mark">0</div>

                <h3>{hasFilters ? "No trips match these filters" : "No ended trips yet"}</h3>

                <p>

                  {hasFilters

                    ? "Change or clear the filters to see more trips."

                    : "Trips show up here once the operations team ends them."}

                </p>

                {hasFilters && (

                  <button className="iv-btn solid" onClick={clearFilters}>

                    Clear filters

                  </button>

                )}

              </div>

            )}

            {!loading &&

              grouped.map(([label, items]) => (

                <div key={label} className="iv-day">

                  <h3 className="iv-day-label">

                    Ended · {label}

                    <em>{items.length}</em>

                  </h3>

                  {items.map((t) => {

                    const c = colorFor(t.agent_id);

                    const has = !!t.invoice_file;

                    return (

                      <article key={t.enq_id} className="iv-card" style={{ "--stripe": c.fg }}>

                        <span className="iv-avatar" style={{ background: c.bg, color: c.fg }}>

                          {initials(t.gustcust_name, t.enq_id)}

                        </span>

                        <div className="iv-card-body">

                          <div className="iv-card-top">

                            <strong className="iv-cust">{t.gustcust_name || "Guest"}</strong>

                            <span className={`iv-status ${has ? "done" : "pending"}`}>

                              {has ? "Invoice uploaded" : "Invoice pending"}

                            </span>

                            <span className="iv-time">{fmtTime(t.trip_end_time)}</span>

                          </div>

                          <div className="iv-contact">

                            {t.guestmob_no && <span>📞 {t.guestmob_no}</span>}

                            <span>💰 {fmtMoney(t.final_price)}</span>

                          </div>

                          {has && (

                            <p className="iv-text">

                              Invoice <strong>{t.invoice_no}</strong> · uploaded {fmtDay(t.invoice_uploaded_at)}

                            </p>

                          )}

                          <div className="iv-card-foot">

                            <span className="iv-chip violet">{t.reference_no}</span>

                            <span className="iv-chip muted">Booking #{t.enq_id}</span>

                            <span className="iv-chip teal">

                              {fmtDate(t.arrive_dt)} → {fmtDate(t.dept_dt)}

                            </span>

                            <span className="iv-spacer" />

                            <button

                              className="iv-by"

                              style={{ color: c.fg, background: c.bg }}

                              onClick={() => selectAgent(t.agent_id)}

                              title="Show only this agent"

                            >

                              {agentLabel(t)}

                              {t.company_name ? ` · ${t.company_name}` : ""}

                            </button>

                          </div>

                          <div className="iv-actions">

                            <button className="iv-btn solid sm" onClick={() => openModal(t)}>

                              {has ? "Replace invoice" : "Upload invoice"}

                            </button>

                            {has && (

                              <>

                                <a

                                  className="iv-btn ghost sm"

                                  href={`${FILE_BASE}${t.invoice_file}`}

                                  target="_blank"

                                  rel="noreferrer"

                                >

                                  View

                                </a>

                                <button className="iv-btn danger sm" onClick={() => removeInvoice(t)}>

                                  Remove

                                </button>

                              </>

                            )}

                          </div>

                        </div>

                      </article>

                    );

                  })}

                </div>

              ))}

          </section>

          {pagination.pages > 1 && (

            <nav className="iv-pager" aria-label="Pagination">

              <span>

                Page {pagination.page} of {pagination.pages} · {pagination.total} trips

              </span>

              <div>

                <button className="iv-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>

                  Previous

                </button>

                <button

                  className="iv-btn solid"

                  disabled={page >= pagination.pages}

                  onClick={() => setPage((p) => p + 1)}

                >

                  Next

                </button>

              </div>

            </nav>

          )}

        </main>

      </div>

      {/* upload modal */}

      {target && (

        <div className="iv-overlay" onMouseDown={closeModal}>

          <form

            className="iv-modal"

            onMouseDown={(e) => e.stopPropagation()}

            onSubmit={submit}

            role="dialog"

            aria-modal="true"

            aria-label="Upload invoice"

          >

            <h3>{target.invoice_file ? "Replace invoice" : "Upload invoice"}</h3>

            <p className="iv-modal-sub">

              {target.reference_no} · {target.gustcust_name || "Guest"} · {fmtMoney(target.final_price)}

              <br />

              {agentLabel(target)}

              {target.company_name ? ` · ${target.company_name}` : ""}

            </p>

            {modalErr && <div className="iv-alert tight">{modalErr}</div>}

            <label className="iv-field">

              <span>Invoice number *</span>

              <input

                className="iv-input"

                value={invoiceNo}

                maxLength={50}

                autoFocus

                onChange={(e) => setInvoiceNo(e.target.value)}

                placeholder="e.g. INV-2026-00123"

              />

            </label>

            <label className="iv-field">

              <span>Invoice file * (PDF, JPG or PNG, max {MAX_MB} MB)</span>

              <input

                className="iv-input file"

                type="file"

                accept=".pdf,.jpg,.jpeg,.png"

                onChange={onPickFile}

              />

              {file && <small className="iv-file">📄 {file.name}</small>}

              {target.invoice_file && !file && (

                <small className="iv-hint">The current file is replaced when you upload a new one.</small>

              )}

            </label>

            <div className="iv-modal-foot">

              <button type="button" className="iv-btn ghost" onClick={closeModal} disabled={saving}>

                Cancel

              </button>

              <button type="submit" className="iv-btn solid" disabled={saving}>

                {saving ? "Uploading…" : "Upload"}

              </button>

            </div>

          </form>

        </div>

      )}

      {toast && <div className={`iv-toast ${toast.type}`}>{toast.msg}</div>}

    </div>

  );

}

function Stat({ tone, value, label }) {

  return (

    <div className={`iv-stat ${tone}`}>

      <strong>{value}</strong>

      <span>{label}</span>

    </div>

  );

}

/* ----------------------------- styles ----------------------------- */

const css = `

@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.iv {

  --ink: #1F1637;

  --ink-2: #5E5778;

  --line: #ECE6F5;

  --bg: #F8F4FF;

  --card: #FFFFFF;

  --pink: #E0246B;

  --violet: #6B3FE0;

  --violet-d: #5128C4;

  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;

  color: var(--ink);

  background: var(--bg);

  min-height: 100vh;

  font-size: 14px;

  line-height: 1.5;

}

.iv *, .iv *::before, .iv *::after { box-sizing: border-box; }

.iv h1, .iv h2, .iv h3, .iv p { margin: 0; }

.iv button { font-family: inherit; cursor: pointer; }

.iv button:disabled { cursor: not-allowed; opacity: .5; }

.iv :focus-visible { outline: 3px solid #C3A9FF; outline-offset: 2px; }

/* header */

.iv-head {

  background: linear-gradient(120deg, #6B3FE0 0%, #B02BD8 55%, #FF3D8B 100%);

  color: #fff;

  padding: 28px 32px 60px;

  display: flex; flex-wrap: wrap; gap: 24px 40px;

  align-items: flex-end; justify-content: space-between;

}

.iv-eyebrow { font-size: 12px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; opacity: .85; }

.iv-head h1 { font-size: 32px; font-weight: 800; letter-spacing: -0.02em; margin-top: 2px; }

.iv-head p { margin-top: 4px; color: #F1E6FF; max-width: 52ch; }

.iv-stats { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 12px; flex: 1 1 540px; max-width: 720px; }

.iv-stat { border-radius: 16px; padding: 14px 16px; display: flex; flex-direction: column; gap: 2px; color: #fff; box-shadow: 0 6px 18px rgba(30, 0, 80, .18); }

.iv-stat strong { font-size: 28px; font-weight: 800; line-height: 1.1; }

.iv-stat span { font-size: 12.5px; font-weight: 600; opacity: .95; }

.iv-stat.pink { background: #E0246B; }

.iv-stat.violet { background: #5B34D6; }

.iv-stat.teal { background: #0A9A85; }

.iv-stat.orange { background: #E27A00; }

/* layout */

.iv-body {

  display: grid; grid-template-columns: 330px minmax(0, 1fr); gap: 20px;

  padding: 0 32px 40px; margin-top: -34px; align-items: start;

}

/* rail */

.iv-rail {

  background: var(--card); border: 1px solid var(--line); border-radius: 18px;

  overflow: hidden; position: sticky; top: 16px; max-height: calc(100vh - 32px);

  display: flex; flex-direction: column; box-shadow: 0 6px 20px rgba(80, 40, 160, .08);

}

.iv-rail-top { padding: 14px; border-bottom: 1px solid var(--line); }

.iv-agent-list { list-style: none; margin: 0; padding: 8px; overflow-y: auto; }

.iv-agent {

  width: 100%; display: flex; align-items: center; gap: 12px; padding: 10px;

  border: 0; background: transparent; border-radius: 14px; text-align: left; color: var(--ink);

  transition: background .15s;

}

.iv-agent:hover { background: #F5F0FF; }

.iv-agent.is-active { background: var(--agent-bg, #EDE4FF); box-shadow: inset 4px 0 0 var(--agent-fg, var(--violet)); }

.iv-agent-main { display: flex; flex-direction: column; min-width: 0; flex: 1; gap: 1px; }

.iv-agent-main strong { font-weight: 700; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.iv-agent-main small { color: var(--ink-2); font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.iv-meter { display: block; height: 5px; background: #F0EAFA; border-radius: 3px; margin-top: 5px; overflow: hidden; }

.iv-meter span { display: block; height: 100%; border-radius: 3px; }

.iv-count { color: #fff; font-weight: 800; font-size: 12.5px; min-width: 28px; height: 26px; padding: 0 8px; border-radius: 13px; display: grid; place-items: center; }

.iv-count.warn { background: #E27A00; }

.iv-rail-empty { padding: 16px; color: var(--ink-2); }

.iv-avatar {

  width: 40px; height: 40px; flex: none; border-radius: 13px;

  display: grid; place-items: center; font-weight: 800; font-size: 14px;

}

.iv-avatar.all { background: linear-gradient(135deg, #6B3FE0, #FF3D8B); color: #fff; }

.iv-avatar.lg { width: 58px; height: 58px; border-radius: 18px; font-size: 19px; background: var(--b-fg); color: #fff; }

/* banner */

.iv-banner {

  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;

  background: var(--b-bg); border-radius: 18px; padding: 18px 20px;

}

.iv-banner-text { flex: 1 1 240px; min-width: 0; }

.iv-banner h2 { font-size: 21px; font-weight: 800; color: var(--b-fg); letter-spacing: -0.01em; }

.iv-banner p { color: var(--ink-2); font-size: 13px; margin-top: 2px; }

.iv-mini { display: flex; gap: 8px; flex-wrap: wrap; }

.iv-mini div { background: rgba(255,255,255,.75); border-radius: 12px; padding: 8px 14px; text-align: center; min-width: 76px; }

.iv-mini strong { display: block; font-size: 19px; font-weight: 800; color: var(--b-fg); line-height: 1.2; }

.iv-mini span { font-size: 11.5px; font-weight: 700; color: var(--ink-2); }

/* filters */

.iv-filters {

  display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;

  background: var(--card); border: 1px solid var(--line); border-radius: 18px;

  padding: 14px; margin-top: 14px;

}

.iv-search { flex: 1 1 260px; }

.iv-input, .iv-date input {

  width: 100%; height: 40px; border: 1.5px solid var(--line); border-radius: 11px;

  padding: 0 12px; font: inherit; color: var(--ink); background: #fff;

}

.iv-input.file { height: auto; padding: 8px 12px; }

.iv-input:focus, .iv-date input:focus { border-color: var(--violet); outline: none; box-shadow: 0 0 0 3px #E3D8FF; }

.iv-date { display: flex; flex-direction: column; gap: 2px; font-size: 12px; font-weight: 600; color: var(--ink-2); }

.iv-date input { width: 150px; }

.iv-seg { display: inline-flex; background: #F1EBFB; border-radius: 11px; padding: 3px; height: 40px; }

.iv-seg button { border: 0; background: transparent; padding: 0 13px; border-radius: 9px; font-weight: 700; color: var(--ink-2); }

.iv-seg button.is-on { background: var(--violet); color: #fff; }

/* buttons */

.iv-btn {

  display: inline-flex; align-items: center; justify-content: center; text-decoration: none;

  height: 40px; padding: 0 16px; border-radius: 11px; font-weight: 700; font-size: 13.5px; border: 1.5px solid transparent;

}

.iv-btn.sm { height: 34px; padding: 0 14px; font-size: 13px; }

.iv-btn.solid { background: var(--violet); color: #fff; }

.iv-btn.solid:hover:not(:disabled) { background: var(--violet-d); }

.iv-btn.ghost { background: #fff; color: var(--violet); border-color: var(--violet); }

.iv-btn.ghost:hover:not(:disabled) { background: #F5F0FF; }

.iv-btn.danger { background: #fff; color: #C4194F; border-color: #F3B4C8; }

.iv-btn.danger:hover { background: #FFF0F5; }

.iv-btn.link { background: transparent; color: var(--violet); padding: 0 8px; }

.iv-alert { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 14px; padding: 12px 16px; border-radius: 12px; background: #FFE9EE; color: #A3173F; font-weight: 600; }

.iv-alert.tight { margin: 0 0 12px; padding: 9px 12px; font-size: 13px; }

/* list */

.iv-list { margin-top: 8px; }

.iv-day { margin-top: 18px; }

.iv-day-label { font-size: 13px; font-weight: 700; color: var(--ink-2); margin-bottom: 10px; display: flex; align-items: center; gap: 10px; }

.iv-day-label em { font-style: normal; background: #EDE4FF; color: var(--violet); border-radius: 999px; padding: 1px 9px; font-size: 12px; }

.iv-day-label::after { content: ""; flex: 1; height: 1px; background: var(--line); }

.iv-card {

  display: flex; gap: 14px; background: var(--card); border: 1px solid var(--line);

  border-left: 5px solid var(--stripe, var(--line)); border-radius: 16px;

  padding: 16px; margin-bottom: 10px; box-shadow: 0 2px 8px rgba(80, 40, 160, .05);

}

.iv-card-body { flex: 1; min-width: 0; }

.iv-card-top { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; }

.iv-cust { font-weight: 800; font-size: 15.5px; }

.iv-time { margin-left: auto; color: var(--ink-2); font-size: 12.5px; font-weight: 600; }

.iv-status { font-size: 11.5px; font-weight: 800; padding: 2px 10px; border-radius: 999px; }

.iv-status.pending { background: #FFF0CC; color: #9A6700; }

.iv-status.done { background: #D2F5EE; color: #0A7F6C; }

.iv-contact { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 4px; color: var(--ink-2); font-size: 13px; font-weight: 600; }

.iv-text { margin-top: 8px; font-size: 14px; overflow-wrap: anywhere; }

.iv-card-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }

.iv-spacer { flex: 1; }

.iv-chip { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; }

.iv-chip.violet { background: #EDE4FF; color: #5128C4; }

.iv-chip.teal { background: #D2F5EE; color: #0A7F6C; }

.iv-chip.muted { background: #F1EEF7; color: var(--ink-2); }

.iv-by { border: 0; font-size: 12px; font-weight: 800; padding: 4px 12px; border-radius: 999px; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.iv-by:hover { filter: brightness(.95); text-decoration: underline; }

.iv-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--line); }

/* empty + skeleton */

.iv-empty { text-align: center; padding: 56px 20px; background: var(--card); border: 1px dashed #D5C9EC; border-radius: 18px; margin-top: 18px; }

.iv-empty-mark { width: 64px; height: 64px; margin: 0 auto 14px; border-radius: 20px; background: #FFE0EC; color: var(--pink); display: grid; place-items: center; font-size: 26px; font-weight: 800; }

.iv-empty h3 { font-size: 18px; font-weight: 800; }

.iv-empty p { color: var(--ink-2); margin: 6px auto 16px; max-width: 40ch; }

.iv-skel { background: linear-gradient(90deg, #F0EAFA 25%, #FAF7FF 50%, #F0EAFA 75%); background-size: 200% 100%; animation: iv-shimmer 1.3s infinite; border-radius: 8px; display: block; }

.iv-skel.circle { width: 40px; height: 40px; border-radius: 13px; flex: none; }

.iv-skel.line { height: 12px; }

.iv-skel.w40 { width: 40%; } .iv-skel.w80 { width: 80%; }

.iv-skel-col { flex: 1; display: flex; flex-direction: column; gap: 10px; padding-top: 6px; }

.iv-skel-row { display: flex; align-items: center; gap: 12px; padding: 10px; }

.iv-skel-row .line { flex: 1; }

@keyframes iv-shimmer { to { background-position: -200% 0; } }

/* pager */

.iv-pager { display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; align-items: center; margin-top: 18px; color: var(--ink-2); font-weight: 600; }

.iv-pager div { display: flex; gap: 8px; }

/* modal */

.iv-overlay { position: fixed; inset: 0; background: rgba(31, 22, 55, .55); display: grid; place-items: center; padding: 16px; z-index: 1000; }

.iv-modal { background: #fff; border-radius: 20px; padding: 24px; width: 100%; max-width: 440px; box-shadow: 0 20px 60px rgba(30, 0, 80, .3); }

.iv-modal h3 { font-size: 20px; font-weight: 800; }

.iv-modal-sub { color: var(--ink-2); font-size: 13px; margin: 4px 0 16px; }

.iv-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; font-size: 13px; font-weight: 700; }

.iv-file { color: #0A7F6C; font-weight: 600; }

.iv-hint { color: var(--ink-2); font-weight: 500; }

.iv-modal-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; }

.iv-toast { position: fixed; top: 20px; right: 20px; z-index: 1100; max-width: 360px; padding: 12px 18px; border-radius: 12px; font-weight: 700; color: #fff; box-shadow: 0 10px 30px rgba(0,0,0,.2); }

.iv-toast.ok { background: #0A8F7B; }

.iv-toast.bad { background: #C4194F; }

/* responsive */

@media (max-width: 980px) {

  .iv-head { padding: 24px 18px 56px; }

  .iv-stats { grid-template-columns: repeat(2, 1fr); }

  .iv-body { grid-template-columns: 1fr; padding: 0 18px 32px; }

  .iv-rail { position: static; max-height: none; }

  .iv-agent-list { display: flex; gap: 8px; overflow-x: auto; padding: 10px; }

  .iv-agent { width: 250px; flex: none; border: 1px solid var(--line); }

  .iv-skel-row, .iv-rail-empty { flex: none; }

}

@media (max-width: 560px) {

  .iv-date, .iv-date input { width: 100%; }

  .iv-date { flex: 1 1 140px; }

  .iv-time { margin-left: 0; width: 100%; }

}

@media (prefers-reduced-motion: reduce) {

  .iv-skel { animation: none; }

}

`;
